/**
 * Owner: Masa Bayu
 * File: author-discovery-labels.ts
 * Date:Time: 2026-10-23 00:11
 * Description: §411–§412 — set label dua skrin author awam baharu
 *   (Author Discovery /author + Author Detail /author/:id). Corak locale sama
 *   dengan marketplace: default English, auto BM ikut negara.
 *   §466 S1 — label rating/reads/ulasan-ringkasan DAN pengikut/emel/DEMO
 *   dibuang; tambah priceUnknown, reviewsEmpty, bioEmpty, categoriesStat
 *   (keadaan kosong jujur + kiraan yang deriva daripada katalog).
 */

import type { MarketplaceLanguage } from '../../home/marketplace/marketplace-locale';

export interface DiscoveryLabels {
  readonly heroTitleA: string;
  readonly heroTitleB: string;
  readonly heroCopy: string;
  readonly searchPlaceholder: string;
  readonly featuredHeading: string;
  readonly popularHeading: string;
  readonly recentHeading: string;
  readonly categoriesHeading: string;
  readonly latestBooksHeading: string;
  readonly findHeading: string;
  readonly findPlaceholder: string;
  readonly viewAll: string;
  readonly follow: string;
  readonly following: string;
  readonly booksUnit: string;
  readonly booksLine: string;
  readonly authorsUnit: string;
  readonly priceUnknown: string;
  readonly more: string;
  readonly pills: Record<'all' | 'fiction' | 'nonfiction' | 'popular', string>;
  readonly emptyAuthors: string;
  readonly emptyBooks: string;
  readonly heroAria: string;
  readonly recentAria: string;
  readonly categoriesAria: string;
  readonly latestBooksAria: string;
}

export interface ProfileLabels {
  readonly screenTitle: string;
  readonly authorWord: string;
  readonly follow: string;
  readonly following: string;
  readonly moreAria: string;
  readonly copyLink: string;
  readonly linkCopied: string;
  readonly alerts: string;
  readonly alertsOn: string;
  readonly tabBooks: string;
  readonly tabAbout: string;
  readonly tabReviews: string;
  readonly tabsAria: string;
  readonly latestBooks: string;
  readonly viewAll: string;
  readonly read: string;
  readonly priceUnknown: string;
  readonly totalBooks: string;
  readonly booksStat: string;
  readonly categoriesStat: string;
  readonly similarHeading: string;
  readonly aboutHeading: string;
  readonly bioHeading: string;
  readonly bioEmpty: string;
  readonly genresHeading: string;
  readonly contactHeading: string;
  readonly websiteLabel: string;
  readonly reviewsHeading: string;
  readonly reviewsEmpty: string;
  readonly noBooks: string;
  readonly noGenres: string;
  readonly noSimilar: string;
  readonly notFoundTitle: string;
  readonly notFoundSub: string;
  readonly backToAuthors: string;
  readonly booksListAria: string;
  readonly similarAria: string;
}

export interface AuthorScreensLabels {
  readonly discovery: DiscoveryLabels;
  readonly profile: ProfileLabels;
}

const EN: AuthorScreensLabels = {
  discovery: {
    heroTitleA: 'Discover',
    heroTitleB: 'Great Authors',
    heroCopy:
      'Explore your favorite authors and find new voices that inspire, educate and entertain.',
    searchPlaceholder: 'Search authors, books or topics...',
    featuredHeading: 'Featured Authors',
    popularHeading: 'Most Published Authors',
    recentHeading: 'Recently Published Authors',
    categoriesHeading: 'Popular Categories',
    latestBooksHeading: 'Latest Books by Author',
    findHeading: 'Find an Author',
    findPlaceholder: 'Search by author name...',
    viewAll: 'View All',
    follow: 'Follow',
    following: 'Following',
    booksUnit: 'Books',
    booksLine: 'books',
    authorsUnit: 'authors',
    priceUnknown: 'Price not set',
    more: 'More',
    pills: {
      all: 'All',
      fiction: 'Fiction',
      nonfiction: 'Non-Fiction',
      popular: 'Most Titles',
    },
    emptyAuthors: 'No authors match your search yet.',
    emptyBooks: 'No books in this category yet.',
    heroAria: 'Author search',
    recentAria: 'Recently published authors',
    categoriesAria: 'Popular categories',
    latestBooksAria: 'Latest books by author',
  },
  profile: {
    screenTitle: 'Author',
    authorWord: 'Author',
    follow: 'Follow',
    following: 'Following',
    moreAria: 'More author actions',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    alerts: 'New-book alerts',
    alertsOn: 'On',
    tabBooks: 'Books',
    tabAbout: 'About',
    tabReviews: 'Reviews',
    tabsAria: 'Author sections',
    latestBooks: 'Latest Books',
    viewAll: 'View All',
    read: 'Read',
    priceUnknown: 'Price not set',
    totalBooks: 'Total Books',
    booksStat: 'Books',
    categoriesStat: 'Categories',
    similarHeading: 'Similar Authors',
    aboutHeading: 'About',
    bioHeading: 'Biography',
    bioEmpty: 'This author has not published a biography on QXRead yet.',
    genresHeading: 'Genres',
    contactHeading: 'Profile Link',
    websiteLabel: 'QXRead profile',
    reviewsHeading: 'Reader Reviews',
    reviewsEmpty:
      'No reader reviews yet. QXRead only shows reviews collected through its own service.',
    noBooks: 'This author has no published books yet.',
    noGenres: 'No category has been published for these titles yet.',
    noSimilar: 'No other author shares a category in the catalog yet.',
    notFoundTitle: 'Author not found',
    notFoundSub: 'This author profile is not in the catalog.',
    backToAuthors: 'Browse all authors',
    booksListAria: 'Published books',
    similarAria: 'Similar authors',
  },
};

const MS: AuthorScreensLabels = {
  discovery: {
    heroTitleA: 'Terokai',
    heroTitleB: 'Penulis Hebat',
    heroCopy:
      'Jelajahi penulis kegemaran anda dan temukan suara baharu yang memberi inspirasi, mendidik dan menghiburkan.',
    searchPlaceholder: 'Cari penulis, buku atau topik...',
    featuredHeading: 'Penulis Unggulan',
    popularHeading: 'Paling Banyak Terbit',
    recentHeading: 'Penulis Terbit Terkini',
    categoriesHeading: 'Kategori Popular',
    latestBooksHeading: 'Buku Terkini Mengikut Penulis',
    findHeading: 'Cari Penulis',
    findPlaceholder: 'Cari mengikut nama penulis...',
    viewAll: 'Lihat Semua',
    follow: 'Ikut',
    following: 'Diikuti',
    booksUnit: 'Buku',
    booksLine: 'buku',
    authorsUnit: 'penulis',
    priceUnknown: 'Harga belum ditetapkan',
    more: 'Lagi',
    pills: {
      all: 'Semua',
      fiction: 'Fiksyen',
      nonfiction: 'Bukan Fiksyen',
      popular: 'Paling Banyak Tajuk',
    },
    emptyAuthors: 'Tiada penulis sepadan dengan carian anda.',
    emptyBooks: 'Tiada buku dalam kategori ini lagi.',
    heroAria: 'Carian penulis',
    recentAria: 'Penulis terbit terkini',
    categoriesAria: 'Kategori popular',
    latestBooksAria: 'Buku terkini mengikut penulis',
  },
  profile: {
    screenTitle: 'Penulis',
    authorWord: 'Penulis',
    follow: 'Ikut',
    following: 'Diikuti',
    moreAria: 'Tindakan penulis lain',
    copyLink: 'Salin pautan',
    linkCopied: 'Pautan disalin',
    alerts: 'Notifikasi buku baharu',
    alertsOn: 'Hidup',
    tabBooks: 'Buku',
    tabAbout: 'Tentang',
    tabReviews: 'Ulasan',
    tabsAria: 'Seksyen penulis',
    latestBooks: 'Buku Terkini',
    viewAll: 'Lihat Semua',
    read: 'Baca',
    priceUnknown: 'Harga belum ditetapkan',
    totalBooks: 'Jumlah Buku',
    booksStat: 'Buku',
    categoriesStat: 'Kategori',
    similarHeading: 'Penulis Serupa',
    aboutHeading: 'Tentang',
    bioHeading: 'Biografi',
    bioEmpty: 'Penulis ini belum menerbitkan biografi di QXRead.',
    genresHeading: 'Genre',
    contactHeading: 'Pautan Profil',
    websiteLabel: 'Profil QXRead',
    reviewsHeading: 'Ulasan Pembaca',
    reviewsEmpty:
      'Tiada ulasan pembaca lagi. QXRead hanya memaparkan ulasan yang dikumpul melalui servisnya sendiri.',
    noBooks: 'Penulis ini belum mempunyai buku terbit.',
    noGenres: 'Tiada kategori diterbitkan untuk tajuk-tajuk ini lagi.',
    noSimilar: 'Tiada penulis lain berkongsi kategori dalam katalog lagi.',
    notFoundTitle: 'Penulis tidak ditemui',
    notFoundSub: 'Profil penulis ini tiada dalam katalog.',
    backToAuthors: 'Lihat semua penulis',
    booksListAria: 'Buku terbit',
    similarAria: 'Penulis serupa',
  },
};

export const AUTHOR_SCREEN_LABELS: Readonly<Record<MarketplaceLanguage, AuthorScreensLabels>> =
  Object.freeze({ en: EN, ms: MS });
